import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, CircularProgress, Tooltip, Dialog, TablePagination, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS } from './shared/MenuLayout';
import { PharmacySelect, PharmacyMetric } from './shared/PharmacyUI';
import PaymentReceiptPanel from './shared/PaymentReceiptPanel';
import { ConsultationRing, ConsultationBars } from './shared/ConsultationCharts';
import './ConsultaCpe.css';

const fmtS = (v) => `S/ ${Number(v||0).toLocaleString('es-PE',{minimumFractionDigits:2})}`;
const hoy = () => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const hace30 = () => { const d=new Date(); d.setDate(d.getDate()-30); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const fmtFecha = (s) => { if(!s) return '—'; const [f]=s.split(' '); return f; };
const COLORS = ['#729DCE','#71AF98','#D2AE78','#CD8593','#9C93C9','#78B6C3'];

// ── DatePickerIOS ──────────────────────────────────────────────────────────────
const DIAS_SEMANA = ['D','L','M','M','J','V','S'];
const MESES_LARGO = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const toYMD = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const parseYMD = (s) => { if(!s) return new Date(); const [y,m,d]=s.split('-').map(Number); return new Date(y,m-1,d); };

const DatePickerIOS = ({ value, onChange, w='100%' }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = useRef(null);
  const popupRef = useRef(null);
  const selDate = value ? parseYMD(value) : null;
  const [viewMonth, setViewMonth] = useState(selDate ? selDate.getMonth() : new Date().getMonth());
  const [viewYear, setViewYear] = useState(selDate ? selDate.getFullYear() : new Date().getFullYear());

  useEffect(() => {
    const h = e => {
      const dentroTrigger = ref.current && ref.current.contains(e.target);
      const dentroPopup = popupRef.current && popupRef.current.contains(e.target);
      if (!dentroTrigger && !dentroPopup) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleOpen = () => {
    if (ref.current) setRect(ref.current.getBoundingClientRect());
    if (selDate) { setViewMonth(selDate.getMonth()); setViewYear(selDate.getFullYear()); }
    setOpen(v=>!v);
  };
  const cambiarMes = (delta) => {
    let m = viewMonth + delta, y = viewYear;
    if (m < 0) { m = 11; y--; } else if (m > 11) { m = 0; y++; }
    setViewMonth(m); setViewYear(y);
  };
  const elegirDia = (dia) => { onChange(toYMD(new Date(viewYear, viewMonth, dia))); setOpen(false); };

  const hoyD = new Date();
  const primerDiaSemana = new Date(viewYear, viewMonth, 1).getDay();
  const totalDias = new Date(viewYear, viewMonth+1, 0).getDate();
  const celdas = [...Array(primerDiaSemana).fill(null), ...Array.from({length:totalDias},(_,i)=>i+1)];
  const labelFecha = selDate ? `${String(selDate.getDate()).padStart(2,'0')}/${String(selDate.getMonth()+1).padStart(2,'0')}/${selDate.getFullYear()}` : '';

  return (
    <Box ref={ref} sx={{ position:'relative', width:w }}>
      <Box onClick={handleOpen}
        sx={{ height:34, px:1.2, display:'flex', alignItems:'center', gap:0.8,
          border:`1px solid ${open?IOS.blue:IOS.separator}`, borderRadius:'8px',
          bgcolor:IOS.surface, cursor:'pointer', userSelect:'none',
          boxShadow:'none', transition:'all 0.15s', boxSizing:'border-box' }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.label2} strokeWidth="2" strokeLinecap="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <Typography sx={{ fontSize:'0.8rem', color:IOS.label, flex:1, whiteSpace:'nowrap' }}>{labelFecha}</Typography>
      </Box>
      {open && rect && ReactDOM.createPortal(
        <Box ref={popupRef} sx={{ position:'fixed', top:rect.bottom+4, left:rect.left, zIndex:99999,
          bgcolor:IOS.surface, border:`1px solid ${IOS.separator}`, borderRadius:'14px',
          boxShadow:'0 8px 24px rgba(0,0,0,0.16)', width:260, p:1.5 }}>
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1 }}>
            <Box component="button" onClick={()=>cambiarMes(-1)} sx={{ width:26, height:26, borderRadius:'8px', border:'none', bgcolor:IOS.bg, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="3" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            </Box>
            <Typography sx={{ fontSize:'0.82rem', fontWeight:700, color:IOS.label }}>{MESES_LARGO[viewMonth]} {viewYear}</Typography>
            <Box component="button" onClick={()=>cambiarMes(1)} sx={{ width:26, height:26, borderRadius:'8px', border:'none', bgcolor:IOS.bg, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="3" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
            </Box>
          </Box>
          <Box sx={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', mb:0.5 }}>
            {DIAS_SEMANA.map((d,i)=>(<Typography key={i} sx={{ fontSize:'0.62rem', fontWeight:700, color:IOS.label2, textAlign:'center' }}>{d}</Typography>))}
          </Box>
          <Box sx={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'2px' }}>
            {celdas.map((dia,i)=>{
              if (dia===null) return <Box key={i} />;
              const esHoy = dia===hoyD.getDate() && viewMonth===hoyD.getMonth() && viewYear===hoyD.getFullYear();
              const esSel = selDate && dia===selDate.getDate() && viewMonth===selDate.getMonth() && viewYear===selDate.getFullYear();
              return (
                <Box key={i} onClick={()=>elegirDia(dia)}
                  sx={{ height:30, display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'8px', cursor:'pointer',
                    bgcolor:esSel?IOS.blue:'transparent', border:esHoy&&!esSel?`1.5px solid ${IOS.blue}`:'none' }}>
                  <Typography sx={{ fontSize:'0.78rem', fontWeight:esSel?700:500, color:esSel?'white':IOS.label }}>{dia}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>, document.body
      )}
    </Box>
  );
};

// ── Sel custom ───────────────────────────────────────────────────────────────
const Sel = props => <PharmacySelect {...props}/>;

// ── KPI / Pills / Modal ────────────────────────────────────────────────────────
const KpiCard = props => <PharmacyMetric {...props}/>;
const PillAfect = ({ val }) => {
  const ok = val === 'AFECTA';
  return (
    <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.5, px:0.8, py:0.2, borderRadius:'20px', bgcolor:ok?'#EDFAF2':'#FFF8E6', border:`1px solid ${ok?'#B7EFD0':'#FFE9A0'}` }}>
      <Box sx={{ width:4, height:4, borderRadius:'50%', bgcolor:ok?IOS.green:IOS.orange }}/>
      <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:ok?'#1A6B3C':'#8B6914' }}>{val}</Typography>
    </Box>
  );
};
const PillEstado = ({ val }) => {
  const ok = (val||'').toUpperCase().startsWith('ACTIV');
  return (
    <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.5, px:0.8, py:0.2, borderRadius:'20px', bgcolor:ok?`${IOS.blue}12`:'#FFF0EF', border:`1px solid ${ok?`${IOS.blue}30`:'#FFD4D1'}` }}>
      <Box sx={{ width:4, height:4, borderRadius:'50%', bgcolor:ok?IOS.blue:IOS.red }}/>
      <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:ok?IOS.blue:'#C0392B' }}>{val}</Typography>
    </Box>
  );
};

const Modal = ({open,onClose,children,w=380}) => <Dialog open={Boolean(open)} onClose={onClose} className="consultation-dialog" maxWidth={false} PaperProps={{sx:{width:w,maxWidth:'calc(100vw - 24px)',borderRadius:'24px',m:1.5}}}>{children}</Dialog>;
const ModalHeader = ({ title, onClose, icon, color=IOS.blue }) => (
  <Box sx={{ px:2.5, pt:2.5, pb:2, borderBottom:`1px solid ${IOS.separator}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
    <Box sx={{ display:'flex', alignItems:'center', gap:1.2 }}>
      <Box sx={{ width:32, height:32, borderRadius:'10px', bgcolor:`${color}15`, display:'flex', alignItems:'center', justifyContent:'center' }}>{icon}</Box>
      <Typography sx={{ fontWeight:700, fontSize:'0.92rem', color:IOS.label }}>{title}</Typography>
    </Box>
    <Box component="button" className="consultation-close" aria-label="Cerrar ventana" onClick={onClose} sx={{ width:28, height:28, borderRadius:'50%', border:'none', bgcolor:'#E5E5EA', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
    </Box>
  </Box>
);

// ══════════════════════════════════════════════════════════════════════════════
const ConsultaComprobantesPaciente = ({ modulo='economia' }) => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const ueId = user?.ueIdEstablecimiento || user?.UeIdEstablecimiento || 0;

  const [ipress, setIpress] = useState([]);
  const [loadIP, setLoadIP] = useState(true);
  const [ipressSel, setIpressSel] = useState('TODAS');
  const [fechaIni, setFechaIni] = useState(hace30());
  const [fechaFin, setFechaFin] = useState(hoy());
  const [dni, setDni] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [buscadoOnce, setBuscadoOnce] = useState(false);
  const [datos, setDatos] = useState([]);
  const [page,setPage]=useState(0),[pageSize,setPageSize]=useState(25),[searchError,setSearchError]=useState('');

  const [tipoGraficoFuente, setTipoGraficoFuente] = useState('stacked');
  const [filtroFP, setFiltroFP] = useState('TODOS');
  const [filtroFuenteRes, setFiltroFuenteRes] = useState('TODOS');
  const [filtroTipoRes, setFiltroTipoRes] = useState('TODOS');
  const [filtroAfectRes, setFiltroAfectRes] = useState('TODOS');
  const [filtroEstadoRes, setFiltroEstadoRes] = useState('TODOS');
  const [filtroCdrRes, setFiltroCdrRes] = useState('TODOS');

  const [modalFormato, setModalFormato] = useState(false);
  const [filaImprimir, setFilaImprimir] = useState(null);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [modalExportar, setModalExportar] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(()=>{
    document.body.style.overflow = panelOpen ? 'hidden' : '';
    return ()=>{ document.body.style.overflow=''; };
  },[panelOpen]);
  const [filaSelec, setFilaSelec] = useState(null);
  const [cdrDisponible, setCdrDisponible] = useState(false);
  const [descargandoDoc, setDescargandoDoc] = useState(null); // 'xml' | 'cdr' | null
  const [detalleVenta, setDetalleVenta] = useState([]);
  const [cargandoDet, setCargandoDet] = useState(false);

  const handleLogout = () => { authService.logout(); navigate(`/${modulo}`); };

  useEffect(() => {
    api.get(`/seguros/ConsumoPaciente/listar-ipress?ueId=${ueId}`)
      .then(r => { if (r.data.success) setIpress(r.data.data || []); })
      .catch(console.error).finally(()=>setLoadIP(false));
  }, [ueId]);

  const buscar = useCallback(async () => {
    if (!dni.trim()) return;
    if(fechaIni>fechaFin){setSearchError('La fecha inicial no puede ser posterior a la final.');return;}
    setSearchError('');setDatos([]);setPage(0);setBuscando(true); setBuscadoOnce(true);
    try {
      const idsEstab = ipressSel === 'TODAS'
        ? ipress.map(ip=>ip.id_establecimiento).join(',')
        : ipressSel;
      const r = await api.get('/seguros/ConsumoPaciente/consulta-cpe', {
        params: { fechaInicio:fechaIni, fechaFin:fechaFin, dni:dni.trim(), idEstablecimiento:idsEstab, ueId }
      });
      if (r.data.success) setDatos(r.data.data || []);
    } catch(e){setSearchError('No se pudo consultar los comprobantes. Intenta nuevamente.');} finally { setBuscando(false); }
  }, [fechaIni, fechaFin, dni, ipressSel, ipress, ueId]);

  const optsFP      = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.fp).filter(Boolean))],[datos]);
  const optsFuente  = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.fuente).filter(Boolean))],[datos]);
  const optsTipo    = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.tipoCPE).filter(Boolean))],[datos]);
  const optsAfect   = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.afectacion).filter(Boolean))],[datos]);
  const optsEstado  = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.estado).filter(Boolean))],[datos]);
  const optsCdr     = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.obsResumen).filter(Boolean))],[datos]);

  const datosFiltrados = useMemo(()=>{
    let f = datos;
    if(filtroFP     !=='TODOS') f = f.filter(d=>d.fp===filtroFP);
    if(filtroFuenteRes!=='TODOS') f = f.filter(d=>d.fuente===filtroFuenteRes);
    if(filtroTipoRes !=='TODOS') f = f.filter(d=>d.tipoCPE===filtroTipoRes);
    if(filtroAfectRes!=='TODOS') f = f.filter(d=>d.afectacion===filtroAfectRes);
    if(filtroEstadoRes!=='TODOS') f = f.filter(d=>d.estado===filtroEstadoRes);
    if(filtroCdrRes  !=='TODOS') f = f.filter(d=>d.obsResumen===filtroCdrRes);
    return f;
  },[datos,filtroFP,filtroFuenteRes,filtroTipoRes,filtroAfectRes,filtroEstadoRes,filtroCdrRes]);

  useEffect(()=>setPage(0),[datosFiltrados]);
  const pageRows=datosFiltrados.slice(page*pageSize,(page+1)*pageSize);
  const kpis = useMemo(() => {
    const activos = datosFiltrados.filter(d=>(d.estado||'').toUpperCase().startsWith('ACTIV'));
    const anulados = datosFiltrados.filter(d=>!(d.estado||'').toUpperCase().startsWith('ACTIV'));
    const total = activos.reduce((s,d)=>s+Number(d.total||0),0);
    return [
      { label:`${datosFiltrados.length} Comprobantes`, valor: datosFiltrados.length, color:IOS.blue,
        icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> },
      { label:'Monto Total', valor: fmtS(total), color:IOS.green,
        icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="1.8" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> },
      { label:`${activos.length} Activos`, valor: activos.length, color:IOS.purple, sub:'comprobantes vigentes',
        icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="1.8" strokeLinecap="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg> },
      { label:`${anulados.length} Anulados`, valor: anulados.length, color:IOS.red,
        icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg> },
    ];
  }, [datosFiltrados]);

  const chartTipo = useMemo(() => {
    const grupos = {};
    datosFiltrados.forEach(d=>{ grupos[d.tipoCPE] = (grupos[d.tipoCPE]||0) + Number(d.total||0); });
    return {
      labels: Object.keys(grupos),
      datasets:[{ data:Object.values(grupos), backgroundColor:COLORS, borderWidth:0, hoverOffset:6 }]
    };
  }, [datosFiltrados]);

  const chartFuente = useMemo(() => {
    const grupos = { AFECTA: { activos:0, anulados:0 }, INAFECTA: { activos:0, anulados:0 } };
    datosFiltrados.forEach(d=>{
      const k = (d.afectacion||'').toUpperCase();
      if(!grupos[k]) grupos[k] = { activos:0, anulados:0 };
      const esActivo = (d.estado||'').toUpperCase().startsWith('ACTIV');
      if(esActivo) grupos[k].activos++; else grupos[k].anulados++;
    });
    const labels = Object.keys(grupos);
    const esApilado = tipoGraficoFuente==='stacked';
    return {
      labels,
      datasets: esApilado
        ? [
            { label:'Activos', data:labels.map(l=>grupos[l].activos), backgroundColor:`${IOS.green}CC`, borderRadius:4, stack:'s', datalabels:{display:false} },
            { label:'Anulados', data:labels.map(l=>grupos[l].anulados), backgroundColor:`${IOS.red}CC`, borderRadius:4, stack:'s', datalabels:{display:false} },
          ]
        : [
            { label:'Comprobantes', data:labels.map(l=>grupos[l].activos+grupos[l].anulados), backgroundColor:`${IOS.blue}CC`, borderRadius:6, datalabels:{display:false} },
          ],
    };
  }, [datosFiltrados, tipoGraficoFuente]);





  const verDetalle = async (row) => {
    setFilaSelec(row); setPanelOpen(true); setCdrDisponible(false);
    setDetalleVenta([]); setCargandoDet(true);
    try {
      const rd = await api.get('/economia/ComprobantesPago/detalle-venta', { params:{ idVenta: row.codigo } });
      if(rd.data.success) setDetalleVenta(rd.data.data);

      if(rd.data.success && rd.data.data?.length>0){
        try {
          const r = await api.get('/economia/ComprobantesPago/verificar-documentos', {
            params:{ tipoCPE: row.tipoCPE, serie:row.serie, numero:row.numero, ueId }
          });
          if(r.data.success) setCdrDisponible(!!r.data.cdrDisponible);
        } catch(e){ console.error(e); setCdrDisponible(false); }
      }
    } catch(e){ console.error(e); }
    finally { setCargandoDet(false); }
  };

  const descargarDocOriginal = async (tipo) => {
    if(!filaSelec) return;
    setDescargandoDoc(tipo);
    try {
      const endpoint = tipo==='xml' ? 'descargar-xml-original' : 'descargar-cdr';
      const params = { tipoCPE:filaSelec.tipoCPE, serie:filaSelec.serie, numero:filaSelec.numero, ueId };
      if(tipo==='xml'){ params.idVenta = filaSelec.codigo; params.modulo = filaSelec.fuente || 'CAJA'; }
      const r = await api.get(`/economia/ComprobantesPago/${endpoint}`, { params, responseType:'blob' });
      const nombre = tipo==='xml' ? `${filaSelec.serie}-${filaSelec.numero}.xml` : `R-${filaSelec.serie}-${filaSelec.numero}.xml`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data],{type:'application/xml'}));
      a.download = nombre; a.click(); URL.revokeObjectURL(a.href);
    } catch(e){
      console.error(e);
      const msg = e?.response?.data?.message || 'No se pudo descargar el documento.';
      alert(msg);
    }
    finally { setDescargandoDoc(null); }
  };

  const imprimir = (row) => { setFilaImprimir(row); setModalFormato(true); };

  const exportarConFiltros = async (tipo) => {
    setExportando(true);
    try {
      const ipressNombre = ipressSel==='TODAS' ? 'Todas las IPRESS' : (ipress.find(ip=>ip.id_establecimiento===ipressSel)?.nombre || ipressSel);
      const payload = {
        datos: datosFiltrados,
        filtroDni: dni.trim(),
        filtroFechaInicio: fechaIni, filtroFechaFin: fechaFin,
        filtroIpress: ipressSel==='TODAS' ? 'TODOS' : ipressNombre,
        filtroFuente: filtroFuenteRes, filtroTipo: filtroTipoRes,
        filtroFormaPago: filtroFP, filtroEstado: filtroEstadoRes,
        filtroRapido: filtroAfectRes, filtroCdr: filtroCdrRes,
      };
      const resp = await api.post(`/seguros/ConsumoPaciente/exportar-${tipo}-cpe`, payload, { responseType:'blob' });
      const ext = tipo==='excel'?'xlsx':'pdf';
      const mime = tipo==='excel'?'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':'application/pdf';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([resp.data],{type:mime}));
      a.download = `ConsultaPaciente_${dni.trim()}.${ext}`;
      a.click(); URL.revokeObjectURL(a.href);
      setModalExportar(false);
    } catch(e){ console.error(e); } finally { setExportando(false); }
  };
  const ejecutarImpresion = async (fmt) => {
    if (!filaImprimir) return; setImprimiendo(true);
    try {
      const r = await api.get('/economia/ComprobantesPago/imprimir', {
        params:{ idVenta:filaImprimir.codigo, fuente:filaImprimir.fuente, tipoCPE:filaImprimir.tipoCPE, formato:fmt, ueId },
        responseType:'blob'
      });
      window.open(URL.createObjectURL(new Blob([r.data],{type:'application/pdf'})), '_blank');
      setModalFormato(false);
    } catch(e){ console.error(e); } finally { setImprimiendo(false); }
  };

  return (
    <>
      <Box className="consultation-page cpe-page" sx={{ minHeight:'100vh', bgcolor:IOS.bg }}>
        <HeaderIOS titulo="Consulta Comprobantes" subtitulo="Economía"
          onHome={()=>navigate(`/${modulo}`)} onLogout={handleLogout}
          nombreCompleto={user?.nombreCompleto||user?.usuario||''}
          avatarLetra={(user?.nombreCompleto||user?.usuario||'U').charAt(0).toUpperCase()} />

        <Box sx={{ maxWidth:1600, mx:'auto', px:{ xs:1.5, sm:2.6 }, pt:2.5, pb:6 }}>

          <div className="cpe-intro"><Typography className="cpe-eyebrow">Consulta · Economía</Typography><Typography component="h1">Cada comprobante, a tu alcance</Typography><Typography>Encuentra los pagos de un paciente y consulta sus documentos en un solo lugar.</Typography></div>
          {/* Filtros */}
          <Box sx={{ bgcolor:IOS.surface, borderRadius:'24px', p:2.5, mb:2.5, boxShadow:'0 6px 24px rgba(32,52,78,0.045)', border:`1px solid ${IOS.separator}` }}>
            <Box sx={{ display:'flex', gap:2, flexWrap:'wrap', alignItems:'flex-end' }}>
              <Box sx={{ display:'flex', flexDirection:'column', gap:0.3 }}>
                <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase' }}>Desde</Typography>
                <DatePickerIOS value={fechaIni} onChange={setFechaIni} w={140} />
              </Box>
              <Box sx={{ display:'flex', flexDirection:'column', gap:0.3 }}>
                <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase' }}>Hasta</Typography>
                <DatePickerIOS value={fechaFin} onChange={setFechaFin} w={140} />
              </Box>
              <Sel label="IPRESS" value={ipressSel} onChange={e=>setIpressSel(e.target.value)} disabled={loadIP} w={240}>
                <option value="TODAS">Todas las IPRESS</option>
                {ipress.map((ip,i)=><option key={i} value={ip.id_establecimiento}>{ip.nombre}</option>)}
              </Sel>
              <Box sx={{ display:'flex', flexDirection:'column', gap:0.3, minWidth:200 }}>
                <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase' }}>DNI del paciente *</Typography>
                <input type="text" placeholder="Ingresa el DNI..." value={dni}
                  onChange={e=>setDni(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&buscar()}
                  style={{ height:34, padding:'0 10px', border:`1px solid ${IOS.separator}`, borderRadius:8, fontSize:'0.8rem', color:IOS.label, backgroundColor:IOS.surface, outline:'none', fontFamily:'monospace' }}
                  onFocus={e=>e.target.style.borderColor=IOS.blue}
                  onBlur={e=>e.target.style.borderColor=IOS.separator} />
              </Box>
              <Box component="button" onClick={buscar} disabled={buscando||!dni.trim()}
                sx={{ height:34, px:2.5, borderRadius:'10px', border:'none', bgcolor:!dni.trim()?'#E5E5EA':IOS.blue,
                  cursor:buscando||!dni.trim()?'default':'pointer', display:'flex', alignItems:'center', gap:0.8,
                  transition:'all 0.15s', '&:hover':{ filter:'brightness(0.92)' } }}>
                {buscando?<CircularProgress size={13} sx={{ color:'white' }} />
                  :<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>}
                <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color:!dni.trim()?'#8E8E93':'white' }}>{buscando?'Buscando...':'Buscar'}</Typography>
              </Box>
            </Box>
          </Box>

          {searchError&&<Alert severity="error" sx={{mb:2}}>{searchError}</Alert>}
          {!buscadoOnce && (
            <Box sx={{ bgcolor:IOS.surface, borderRadius:'24px', py:10, textAlign:'center', border:`1px solid ${IOS.separator}` }}>
              <Box sx={{ width:60, height:60, borderRadius:'50%', bgcolor:`${IOS.blue}12`, display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:2 }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="1.8" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              </Box>
              <Typography sx={{ fontWeight:700, fontSize:'0.95rem', color:IOS.label, mb:0.5 }}>Listo para buscar</Typography>
              <Typography sx={{ color:IOS.label2, fontSize:'0.82rem' }}>Ingresa el DNI del paciente y presiona buscar</Typography>
            </Box>
          )}

          {buscadoOnce && (
            <>
              {/* KPIs */}
              <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr 1fr', sm:'repeat(4,1fr)' }, gap:1.5, mb:2.5 }}>
                {kpis.map(k=><KpiCard key={k.label} {...k} />)}
              </Box>

              {/* Gráficos */}
              {datosFiltrados.length>0 && (
                <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', md:'1fr 1fr' }, gap:{ xs:1.5, md:2 }, mb:2.5 }}>
                  <Box sx={{ bgcolor:IOS.surface, borderRadius:'24px', p:{ xs:1.8, sm:2.5 }, boxShadow:'0 6px 24px rgba(32,52,78,0.045)', border:`1px solid ${IOS.separator}`, minWidth:0 }}>
                    <Typography sx={{ fontWeight:700, fontSize:'0.85rem', color:IOS.label, mb:0.2 }}>Monto por Tipo CPE</Typography>
                    <Typography sx={{ fontSize:'0.7rem', color:IOS.label2, mb:2 }}>Distribución del gasto</Typography>
                    <Box sx={{ height:{ xs:260, sm:260 } }}>
                      <ConsultationRing data={chartTipo}/>
                    </Box>
                  </Box>
                  <Box sx={{ bgcolor:IOS.surface, borderRadius:'24px', p:{ xs:1.8, sm:2.5 }, boxShadow:'0 6px 24px rgba(32,52,78,0.045)', border:`1px solid ${IOS.separator}`, minWidth:0 }}>
                    <Box sx={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', mb:2, gap:1, flexWrap:'wrap' }}>
                      <Box>
                        <Typography sx={{ fontWeight:700, fontSize:'0.85rem', color:IOS.label, mb:0.2 }}>Afectos vs Inafectos</Typography>
                        <Typography sx={{ fontSize:'0.7rem', color:IOS.label2 }}>{tipoGraficoFuente==='stacked'?'Activos vs Anulados':'Total comprobantes'}</Typography>
                      </Box>
                      <Box sx={{ display:'flex', gap:0.4, bgcolor:IOS.bg, borderRadius:'10px', p:0.4, flexShrink:0 }}>
                        {[
                          { key:'bar', tip:'Barras', icon:<><rect x="3" y="10" width="4" height="8" rx="1" fill="currentColor" opacity=".9"/><rect x="10" y="6" width="4" height="12" rx="1" fill="currentColor" opacity=".9"/><rect x="17" y="3" width="4" height="15" rx="1" fill="currentColor" opacity=".9"/></> },
                          { key:'stacked', tip:'Apiladas', icon:<><rect x="3" y="13" width="4" height="5" rx="1" fill="currentColor" opacity=".5"/><rect x="3" y="8" width="4" height="5" rx="1" fill="currentColor"/><rect x="10" y="10" width="4" height="8" rx="1" fill="currentColor" opacity=".5"/><rect x="10" y="5" width="4" height="5" rx="1" fill="currentColor"/></> },
                        ].map(({key,tip,icon})=>(
                          <Tooltip key={key} title={tip} placement="top">
                            <Box component="button" aria-label={tip} aria-pressed={tipoGraficoFuente===key} onClick={()=>setTipoGraficoFuente(key)}
                              sx={{ width:30, height:30, borderRadius:'8px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s',
                                bgcolor:tipoGraficoFuente===key?IOS.surface:'transparent',
                                boxShadow:tipoGraficoFuente===key?'0 1px 3px rgba(0,0,0,0.12)':'none',
                                color:tipoGraficoFuente===key?IOS.blue:IOS.label2 }}>
                              <svg width="16" height="16" viewBox="0 0 24 24">{icon}</svg>
                            </Box>
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>
                    <Box key={tipoGraficoFuente} sx={{ height:{ xs:260, sm:260 } }}>
                      <ConsultationBars data={chartFuente} stacked={tipoGraficoFuente==='stacked'}/>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Filtros sobre resultados */}
              {datos.length>0 && (
                <Box sx={{ bgcolor:IOS.surface, borderRadius:'24px', p:2, mb:2, boxShadow:'0 6px 24px rgba(32,52,78,0.045)', border:`1px solid ${IOS.separator}`,
                  display:{ xs:'grid', sm:'flex' }, gridTemplateColumns:{ xs:'1fr 1fr' }, flexWrap:'wrap', gap:{ xs:1, sm:1.5 }, alignItems:{ sm:'flex-end' } }}>
                  {[
                    ['Forma Pago', filtroFP, setFiltroFP, optsFP, 150],
                    ['Fuente', filtroFuenteRes, setFiltroFuenteRes, optsFuente, 130],
                    ['Tipo CPE', filtroTipoRes, setFiltroTipoRes, optsTipo, 150],
                    ['Afectación', filtroAfectRes, setFiltroAfectRes, optsAfect, 140],
                    ['Estado', filtroEstadoRes, setFiltroEstadoRes, optsEstado, 130],
                    ['CDR', filtroCdrRes, setFiltroCdrRes, optsCdr, 170],
                  ].map(([lbl,val,set,opts,w])=>(
                    <Sel key={lbl} label={lbl} value={val} onChange={e=>set(e.target.value)} w={{ xs:'100%', sm:w }}>
                      {opts.map(o=><option key={o} value={o}>{o}</option>)}
                    </Sel>
                  ))}
                  <Box sx={{ ml:{ sm:'auto' }, gridColumn:{ xs:'1 / -1' }, display:'flex', alignItems:'center', justifyContent:{ xs:'space-between', sm:'flex-end' }, gap:1 }}>
                    <Box sx={{ px:1.5, py:0.6, bgcolor:IOS.bg, borderRadius:'20px', border:`1px solid ${IOS.separator}`, textAlign:'center' }}>
                      <Typography sx={{ fontSize:'0.58rem', color:IOS.label2, display:'block' }}>Filtrados</Typography>
                      <Typography sx={{ fontSize:'0.88rem', fontWeight:700, color:IOS.blue, lineHeight:1 }}>{datosFiltrados.length}</Typography>
                    </Box>
                    <Tooltip title="Exportar resultado" placement="top">
                      <Box component="button" onClick={()=>datosFiltrados.length>0&&setModalExportar(true)}
                        sx={{ width:34, height:34, borderRadius:'12px', border:'none', bgcolor:IOS.blue,
                          cursor:datosFiltrados.length===0?'default':'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                          opacity:datosFiltrados.length===0?0.4:1, transition:'all 0.15s', '&:hover':{ filter:'brightness(0.9)' } }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M12 3v13M7 11l5 5 5-5"/><path d="M4 20h16"/></svg>
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              )}

              {/* Tabla */}
              <Box sx={{ bgcolor:IOS.surface, borderRadius:'24px', boxShadow:'0 6px 24px rgba(32,52,78,0.045)', border:`1px solid ${IOS.separator}`, overflow:'hidden' }}>
                <Box sx={{ px:2.5, py:2, borderBottom:`1px solid ${IOS.separator}` }}>
                  <Typography sx={{ fontWeight:700, fontSize:'0.9rem', color:IOS.label }}>
                    {datosFiltrados.length===0?'Sin resultados':`${datosFiltrados.length} comprobante${datosFiltrados.length!==1?'s':''} encontrado${datosFiltrados.length!==1?'s':''}`}
                  </Typography>
                </Box>
                {buscando
                  ? <Box sx={{ py:10, textAlign:'center' }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
                  : datosFiltrados.length===0
                    ? <Box sx={{ py:10, textAlign:'center' }}><Typography sx={{ color:IOS.label2, fontSize:'0.85rem' }}>No se encontraron comprobantes para este DNI en el período seleccionado</Typography></Box>
                    : <>
    {/* ── Desktop ── */}
    <Box sx={{ display:{xs:'none',md:'block'}, overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.75rem' }}>
        <thead>
          <tr>
            {['IPRESS','Fuente','Tipo','Serie-N°','Fecha','Cliente','Total','Afect.','Estado','CDR',''].map((h,i)=>(
              <th key={i} style={{ padding:'9px 10px', textAlign:i===6?'right':i===7||i===8?'center':'left',
                fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.06em', textTransform:'uppercase',
                backgroundColor:IOS.surface, borderBottom:`1px solid ${IOS.separator}`, whiteSpace:'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row,i)=>(
            <tr key={i} style={{ borderBottom:`1px solid ${IOS.separator}`, backgroundColor:IOS.surface, transition:'background 0.1s' }}
              onMouseEnter={e=>e.currentTarget.style.backgroundColor=IOS.bg}
              onMouseLeave={e=>e.currentTarget.style.backgroundColor=IOS.surface}>
              <td style={{ padding:'9px 10px', color:IOS.label, fontWeight:600, maxWidth:150 }}>
                <span style={{ display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.establecimiento}</span>
              </td>
              <td style={{ padding:'9px 10px', color:IOS.label2 }}>{row.fuente}</td>
              <td style={{ padding:'9px 10px', color:IOS.label }}>{row.tipoCPE}</td>
              <td style={{ padding:'9px 10px', fontFamily:'monospace', color:IOS.label2 }}>{row.serie}-{row.numero}</td>
              <td style={{ padding:'9px 10px', color:IOS.label2, whiteSpace:'nowrap' }}>{fmtFecha(row.fecha)}</td>
              <td style={{ padding:'9px 10px', color:IOS.label, maxWidth:180 }}>
                <Tooltip title={row.cliente} placement="top">
                  <span style={{ display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.cliente}</span>
                </Tooltip>
              </td>
              <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:700, color:IOS.label }}>{Number(row.total||0).toFixed(2)}</td>
              <td style={{ padding:'9px 10px', textAlign:'center' }}><PillAfect val={row.afectacion} /></td>
              <td style={{ padding:'9px 10px', textAlign:'center' }}><PillEstado val={row.estado} /></td>
              <td style={{ padding:'9px 10px', color:IOS.label2, fontSize:'0.68rem', maxWidth:120 }}>
                <span style={{ display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.obsResumen||'—'}</span>
              </td>
              <td style={{ padding:'6px 8px', textAlign:'center' }}>
                <Box sx={{ display:'flex', gap:0.5, justifyContent:'center' }}>
                  <Tooltip title="Ver comprobante" placement="top">
                    <Box onClick={()=>verDetalle(row)} sx={{ width:26, height:26, borderRadius:'50%', bgcolor:`${IOS.blue}12`, display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer', '&:hover':{ bgcolor:`${IOS.blue}22` } }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Imprimir" placement="top">
                    <Box onClick={()=>imprimir(row)} sx={{ width:26, height:26, borderRadius:'50%', bgcolor:`${IOS.green}12`, display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer', '&:hover':{ bgcolor:`${IOS.green}22` } }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                    </Box>
                  </Tooltip>
                </Box>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>

    {/* ── Móvil — cards ── */}
    <Box sx={{ display:{xs:'block',md:'none'}, p:1.5 }}>
      {pageRows.map((row,i)=>(
        <Box key={i} sx={{ bgcolor:IOS.surface, borderRadius:'14px', mb:1.5,
          boxShadow:'0 6px 24px rgba(32,52,78,0.045)', border:`1px solid ${IOS.separator}`,
          borderLeft:`3px solid ${(row.estado||'').toUpperCase().startsWith('ACTIV')?IOS.blue:IOS.red}` }}>
          {/* Cabecera */}
          <Box sx={{ px:2, py:1.2, borderBottom:`1px solid ${IOS.separator}`,
            display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:1 }}>
            <Box sx={{ minWidth:0 }}>
              <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color:IOS.label,
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.cliente}</Typography>
              <Typography sx={{ fontSize:'0.65rem', color:IOS.label2, mt:0.2,
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.establecimiento}</Typography>
            </Box>
            <Box sx={{ display:'flex', gap:0.5, flexShrink:0 }}>
              <Box onClick={()=>verDetalle(row)} sx={{ width:28, height:28, borderRadius:'50%', bgcolor:`${IOS.blue}12`,
                display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer', '&:hover':{ bgcolor:`${IOS.blue}22` } }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              </Box>
              <Box onClick={()=>imprimir(row)} sx={{ width:28, height:28, borderRadius:'50%', bgcolor:`${IOS.green}12`,
                display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer', '&:hover':{ bgcolor:`${IOS.green}22` } }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
              </Box>
            </Box>
          </Box>
          {/* Body */}
          <Box sx={{ px:2, py:1.2 }}>
            <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:1, mb:1 }}>
              <Box sx={{ bgcolor:IOS.bg, borderRadius:'8px', p:1 }}>
                <Typography sx={{ fontSize:'0.55rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', letterSpacing:'0.05em', mb:0.2 }}>Serie-N°</Typography>
                <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:IOS.label, fontFamily:'monospace' }}>{row.serie}-{row.numero}</Typography>
              </Box>
              <Box sx={{ bgcolor:IOS.bg, borderRadius:'8px', p:1 }}>
                <Typography sx={{ fontSize:'0.55rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', letterSpacing:'0.05em', mb:0.2 }}>Fecha</Typography>
                <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:IOS.label }}>{fmtFecha(row.fecha)}</Typography>
              </Box>
              <Box sx={{ bgcolor:IOS.bg, borderRadius:'8px', p:1 }}>
                <Typography sx={{ fontSize:'0.55rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', letterSpacing:'0.05em', mb:0.2 }}>Fuente / Tipo</Typography>
                <Typography sx={{ fontSize:'0.78rem', fontWeight:600, color:IOS.label }}>{row.fuente} · {row.tipoCPE}</Typography>
              </Box>
              <Box sx={{ bgcolor:`${IOS.blue}08`, borderRadius:'8px', p:1, border:`1px solid ${IOS.blue}20` }}>
                <Typography sx={{ fontSize:'0.55rem', fontWeight:700, color:IOS.blue, textTransform:'uppercase', letterSpacing:'0.05em', mb:0.2 }}>Total</Typography>
                <Typography sx={{ fontSize:'0.9rem', fontWeight:800, color:IOS.blue }}>S/ {Number(row.total||0).toFixed(2)}</Typography>
              </Box>
            </Box>
            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', pt:1, borderTop:`1px solid ${IOS.separator}` }}>
              <Box sx={{ display:'flex', gap:0.8 }}>
                <PillAfect val={row.afectacion} />
                <PillEstado val={row.estado} />
              </Box>
              {row.obsResumen && (
                <Typography sx={{ fontSize:'0.65rem', color:IOS.label2, maxWidth:140,
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.obsResumen}</Typography>
              )}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  </>
                }
              </Box>
              <TablePagination component="div" count={datosFiltrados.length} page={Math.min(page,Math.max(0,Math.ceil(datosFiltrados.length/pageSize)-1))} rowsPerPage={pageSize} rowsPerPageOptions={[25,50,100]} onPageChange={(_,v)=>setPage(v)} onRowsPerPageChange={e=>{setPageSize(Number(e.target.value));setPage(0);}} labelRowsPerPage="Filas" labelDisplayedRows={({from,to,count})=>from+'–'+to+' de '+count}/>
            </>
          )}
        </Box>
      </Box>

      <PaymentReceiptPanel {...{panelOpen,setPanelOpen,filaSelec,detalleVenta,cargandoDet,imprimir,descargarDocOriginal,cdrDisponible,descargandoDoc}} requiereCpe={()=>!cargandoDet&&detalleVenta.length>0}/>
      {/* Modal Exportar */}
      <Modal open={modalExportar} onClose={()=>!exportando&&setModalExportar(false)} w={400}>
        <ModalHeader title="Exportar Resultado" onClose={()=>!exportando&&setModalExportar(false)} color={IOS.purple}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="2" strokeLinecap="round"><path d="M12 3v13M7 11l5 5 5-5"/><path d="M4 20h16"/></svg>} />
        <Box sx={{ p:2.5 }}>
          <Typography sx={{ fontSize:'0.8rem', color:IOS.label2, mb:2 }}>
            Se exportarán <strong style={{ color:IOS.label }}>{datosFiltrados.length} registros</strong> con los filtros aplicados — DNI <strong style={{ color:IOS.label }}>{dni}</strong>
          </Typography>
          <Box sx={{ display:'flex', gap:1.5 }}>
            {[{fmt:'excel',label:'Excel',ext:'.xlsx',color:IOS.green},{fmt:'pdf',label:'PDF',ext:'A4 Horizontal',color:IOS.red}].map(({fmt,label,ext,color})=>(
              <Box key={fmt} component="button" onClick={()=>!exportando&&exportarConFiltros(fmt)} disabled={exportando}
                sx={{ flex:1, py:2.5, borderRadius:'14px', border:`2px solid ${color}30`, bgcolor:`${color}08`, cursor:exportando?'default':'pointer',
                  display:'flex', flexDirection:'column', alignItems:'center', gap:1.2, transition:'all 0.2s',
                  '&:hover':{ bgcolor:`${color}14`, borderColor:`${color}60` } }}>
                <Box sx={{ width:50, height:50, borderRadius:'14px', bgcolor:`${color}15`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </Box>
                <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color }}>{exportando?'Generando…':label}</Typography>
                <Typography sx={{ fontSize:'0.65rem', color:IOS.label2 }}>{ext}</Typography>
              </Box>
            ))}
          </Box>
          {exportando&&<Box sx={{ display:'flex', justifyContent:'center', mt:2 }}><CircularProgress size={20} sx={{ color:IOS.purple }} /></Box>}
        </Box>
      </Modal>

      {/* Modal Formato Impresión */}
      <Modal open={modalFormato&&!!filaImprimir} onClose={()=>!imprimiendo&&setModalFormato(false)} w={380}>
        <ModalHeader title="Formato de Impresión" onClose={()=>!imprimiendo&&setModalFormato(false)} color={IOS.blue}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>} />
        <Box sx={{ p:2.5 }}>
          {filaImprimir&&(<>
            <Typography sx={{ fontSize:'0.8rem', color:IOS.label, fontWeight:600, mb:0.3 }}>{filaImprimir.comprobante}</Typography>
            <Typography sx={{ fontSize:'0.74rem', color:IOS.label2, mb:2 }}>{filaImprimir.cliente} — {filaImprimir.fuente}</Typography>
          </>)}
          <Box sx={{ display:'flex', gap:1.5, mb:1 }}>
            {[{fmt:'A4',label:'Formato A4',sub:'Hoja completa',color:IOS.blue},{fmt:'TICKET',label:'Ticket',sub:'80mm / POS',color:IOS.orange}].map(({fmt,label,sub,color})=>(
              <Box key={fmt} component="button" onClick={()=>!imprimiendo&&ejecutarImpresion(fmt)} disabled={imprimiendo}
                sx={{ flex:1, py:2.5, borderRadius:'14px', border:`2px solid ${color}30`, bgcolor:`${color}08`, cursor:imprimiendo?'default':'pointer',
                  display:'flex', flexDirection:'column', alignItems:'center', gap:0.8, transition:'all 0.2s', '&:hover':{ bgcolor:`${color}14`, borderColor:`${color}60` } }}>
                <Box sx={{ width:44, height:44, borderRadius:'12px', bgcolor:`${color}15`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <svg width="22" height={fmt==='A4'?28:34} viewBox={fmt==='A4'?"0 0 16 22":"0 0 12 22"} fill="none">
                    {fmt==='A4' ? <><rect x="1" y="1" width="14" height="20" rx="1.5" fill={`${color}20`} stroke={color} strokeWidth="1"/><line x1="4" y1="6" x2="12" y2="6" stroke={color} strokeWidth="1"/><line x1="4" y1="9" x2="12" y2="9" stroke={color} strokeWidth="1"/></> : <><rect x="1" y="1" width="10" height="20" rx="1.5" fill={`${color}20`} stroke={color} strokeWidth="1"/><line x1="3" y1="5" x2="9" y2="5" stroke={color} strokeWidth="0.8"/></>}
                  </svg>
                </Box>
                <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color }}>{label}</Typography>
                <Typography sx={{ fontSize:'0.65rem', color:IOS.label2 }}>{sub}</Typography>
              </Box>
            ))}
          </Box>
          {imprimiendo&&<Box sx={{ display:'flex', justifyContent:'center', mt:1 }}><CircularProgress size={20} sx={{ color:IOS.blue }} /></Box>}
        </Box>
      </Modal>
    </>
  );
};

export default ConsultaComprobantesPaciente;